import React from 'react'
import { Link } from 'react-router-dom'
import { type GridValidRowModel } from '@mui/x-data-grid'
import { Actions, FieldType, TableModalAction } from './types'
import Box from '@mui/material/Box'
import CellWarning from './CellTypes/CellWarning'
import ROUTES from '../../consts/paths'
import { isArray } from 'lodash'
import LeadText from './CellTypes/LeadText'
import { Typography } from '@mui/material'
import CellBadge from './CellTypes/CellBadge'
import MultiBadge from './CellTypes/MultiBadge'
import { grey } from '../../colors'
import { formatDateByUserTimeZone } from '../../helpers/formatDateByUserTimeZone'
import TextList from './CellTypes/TextList'
import ObjectList from './CellTypes/ObjectList'
import ChipsForAttributes, { type DataItem } from './CellTypes/ChipsForAttributes'
import { type OverflowClickHandler } from './CellTypes/OverflowIndicator'
import { CellWarningType, type CellWarningConstraintType, constraintsMapping } from './CellTypes/constant'

export const cdgCellCommonStyles = (): React.CSSProperties => ({
  fontSize: '14px',
  color: grey[850],
  fontWeight: 400,
  whiteSpace: 'pre-line'
})

export const flattenKeys = (obj: Record<string, any>, parentKey = ''): Record<string, string | null> => {
  let result: Record<string, any> = {}
  for (const key in obj) {
    const newKey = parentKey !== '' ? `${parentKey}.${key}` : key
    if (isArray(obj[key])) {
      // Dotted path supports nested CDG fields (e.g. linked_section.attributes).
      // Bare key preserves legacy lookup for top-level array columns (e.g. attributes).
      result[newKey] = obj[key]
      result[key] = obj[key]
      continue
    }
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const nestedKeys = flattenKeys(obj[key], newKey)
      result = { ...result, ...nestedKeys }
      continue
    } else {
      result[newKey] = obj[key]
      continue
    }
  }
  return result
}

export const toCamelCaseRemoveDotAlso = (str: string): string => {
  return str
    .split(/[._]/)
    .map((word, i) =>
      i === 0
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('')
}

export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-zA-Z])/g, (_, match) => match.toUpperCase())
    .replace(/_(\d)/g, '_$1')
}

export const generateCell = <T,>(id: string, colDef: GridValidRowModel, row: Record<string, any>, setSelectedRow: any, setActionModal: any, columnIndex: number, overrides: Record<string, (a: T) => JSX.Element>, rowLevelWarningKeys: string[] = []): JSX.Element => {
  const { data } = row

  const camelcaseField = toCamelCase(colDef.field)

  const warningMessageObj = data?.warningMessage?.[toCamelCaseRemoveDotAlso(colDef.field)]

  // Check if constraint exists and is a valid CellWarningConstraintType (for backward compatibility)
  const constraint =
    warningMessageObj?.constraint &&
    warningMessageObj.constraint in constraintsMapping
      ? (warningMessageObj.constraint as CellWarningConstraintType)
      : undefined

  const isFirstColumn = columnIndex === 0

  // Only the Text, Link and LinkedEntity branches below render `warningIcon`, so a table passing
  // `rowLevelWarningKeys` needs one of those as its first shown column (course sections: Section ID).
  const rowLevelWarnings = isFirstColumn
    ? rowLevelWarningKeys.map(key => data?.warningMessage?.[key]).filter(Boolean)
    : []
  const cellWarnings = [warningMessageObj, ...rowLevelWarnings].filter(Boolean)
  const warningIcon = cellWarnings.length > 0 && (
    <CellWarning
      constraint={constraint}
      rowId={id}
      keyType={cellWarnings.some(w => w.key === CellWarningType.ERROR) ? CellWarningType.ERROR : CellWarningType.WARNING}
      message={cellWarnings.map(w => w.value).filter((text): text is string => typeof text === 'string' && text !== '')}
    />
  )

  const flattenedData = flattenKeys(data)
  const getFieldData = (): { value: string | string[], link: string } => {
    const value: string = ''
    const link = data?.divisionId ?? data?.sectionId

    if (colDef.field === 'children' && data?.children?.length > 0) {
      return {
        value: data?.children,
        link
      }
    }
    const matchedValue = Object.keys(flattenedData).find(key => key === camelcaseField)

    if (matchedValue !== undefined) {
      const value = flattenedData?.[matchedValue] ?? ''
      return {
        value,
        link
      }
    }

    return {
      value,
      link
    }
  }

  const linkDisabledLinkStyle = {
    textDecoration: 'none',
    cursor: 'pointer'
  }

  const { value, link = '' } = getFieldData()
  const canOpenEditModal = Array.isArray(row?.actions) && row.actions.includes(Actions.EDIT)
  const onOverflowIndicatorClick: OverflowClickHandler | undefined = canOpenEditModal
    ? (event: React.MouseEvent<HTMLElement>): void => {
        event.preventDefault()
        event.stopPropagation()
        setSelectedRow(data)
        setActionModal(TableModalAction.EDIT)
      }
    : undefined

  const override = overrides[camelcaseField]
  if (override !== undefined && override !== null) {
    return overrides[camelcaseField](flattenedData as T)
  }

  const multilineTextStyles = (): React.CSSProperties => ({
    ...cdgCellCommonStyles(),
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  })

  switch (colDef.type) {
    case FieldType.TEXT: {
      const textValue = typeof value === 'string' ? value : ''
      return (
        <>
          {warningIcon}
          {isFirstColumn
            ? <LeadText sx={{ cursor: 'cell !important', whiteSpace: 'pre' }} title={textValue}>{value}</LeadText>
            : (
              <Typography
                style={multilineTextStyles()}
                title={textValue}
              >
                {textValue}
              </Typography>
              )
          }
        </>
      )
    }

    case FieldType.LINK: {
      const textValue = typeof value === 'string' ? value : ''
      return (
        <Link to={`${ROUTES.REPORTING_HIERARCHY}/${link}`}
          style={linkDisabledLinkStyle}
        >
          {warningIcon}
          {isFirstColumn
            ? <LeadText sx={{ whiteSpace: 'pre' }} title={textValue}>{value}</LeadText>
            : (
              <Typography
                style={multilineTextStyles()}
                title={textValue}
              >
                {textValue}
              </Typography>
              )
          }
        </Link>
      )
    }

    case FieldType.LINKED_ENTITY: {
      return (
        <>
          {warningIcon}
          {isFirstColumn
            ? <LeadText sx={{ cursor: 'pointer', whiteSpace: 'pre' }}>{value}</LeadText>
            : <Typography style={cdgCellCommonStyles()}>{value}</Typography>
          }
        </>
      )
    }

    case FieldType.CHIP: {
      // Check if the first element in the value array is an object.
      // If so, render the ChipsForAttributes component, which is likely used to display attributes in chip form.
      if (Array.isArray(value) && typeof value[0] === 'object') {
        return <ChipsForAttributes data={value as unknown as DataItem[]} onOverflowClick={onOverflowIndicatorClick} />
      }

      // Check if the value is an array.
      // If value is an array, render a MultiBadge component that can handle multiple items.
      if (isArray(value)) {
        return <MultiBadge list={value} maxVisible={1} onOverflowClick={onOverflowIndicatorClick} />
      }

      //  Treat the value as a single value and render a CellBadge component.
      return <CellBadge label={value} />
    }

    case FieldType.TEXT_LIST: {
      return <TextList list={value as string[]} onOverflowClick={onOverflowIndicatorClick} />
    }
    case FieldType.OBJECT_LIST: {
      return <ObjectList list={value as unknown as Array<Record<string, any>>} onOverflowClick={onOverflowIndicatorClick} />
    }
    case FieldType.DATE: {
      const isValidDate = (dateString: string): boolean => !isNaN(Date.parse(dateString))
      const formatedDate = isValidDate(value as string) && formatDateByUserTimeZone(value as string, 'MM/dd/yyyy @ h:mm a')
      return (
        <Typography style={cdgCellCommonStyles()}>{formatedDate}</Typography>
      )
    }
    case FieldType.NUMBER: {
      return (
        isFirstColumn
          ? <LeadText sx={{ whiteSpace: 'pre' }}>{value} </LeadText>
          : <Typography style={cdgCellCommonStyles()} >{value}</Typography>
      )
    }
    default: {
      return <Box component="span">  {typeof value === 'string' ? value : ''}</Box>
    }
  }
}
